import { useRef, useCallback, useState, useEffect } from 'react';
import type { ViewportState, Position } from '../types/warehouse';
import {
  MIN_ZOOM,
  MAX_ZOOM,
  ZOOM_STEP,
  DEFAULT_GRID_SIZE,
} from '../types/warehouse';

export interface CanvasState {
  canvasSize: { width: number; height: number };
}

export interface UseCanvasOptions {
  viewport: ViewportState;
  rulerOffset?: number;
  onViewportChange?: (viewport: ViewportState) => void;
}

export interface UseCanvasReturn {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  canvasSize: { width: number; height: number };
  zoomIn: () => void;
  zoomOut: () => void;
  resetView: () => void;
  setZoom: (scale: number) => void;
  panTo: (x: number, y: number) => void;
  centerOn: (x: number, y: number) => void;
  screenToWorld: (screenX: number, screenY: number) => Position;
  worldToScreen: (worldX: number, worldY: number) => Position;
  fitToContent: (bounds: { minX: number; minY: number; maxX: number; maxY: number }) => void;
}

export function useCanvas(options: UseCanvasOptions): UseCanvasReturn {
  const {
    viewport,
    rulerOffset = 0,
    onViewportChange,
  } = options;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<ViewportState>(viewport);

  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const canvasSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    viewportRef.current = viewport;
  }, [viewport]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setCanvasSize({ width, height });
        canvasSizeRef.current = { width, height };

        const canvas = canvasRef.current;
        if (canvas) {
          const dpr = window.devicePixelRatio || 1;
          canvas.width = width * dpr;
          canvas.height = height * dpr;
          canvas.style.width = `${width}px`;
          canvas.style.height = `${height}px`;
        }
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  const zoomIn = useCallback(() => {
    const currentViewport = viewportRef.current;
    const size = canvasSizeRef.current;

    const newScale = Math.min(MAX_ZOOM, currentViewport.scale + ZOOM_STEP);
    const centerX = size.width / 2;
    const centerY = size.height / 2;

    const worldCenterX = (centerX - rulerOffset - currentViewport.x) / currentViewport.scale;
    const worldCenterY = (centerY - rulerOffset - currentViewport.y) / currentViewport.scale;

    const newX = centerX - rulerOffset - worldCenterX * newScale;
    const newY = centerY - rulerOffset - worldCenterY * newScale;

    onViewportChange?.({ x: newX, y: newY, scale: newScale });
  }, [onViewportChange, rulerOffset]);

  const zoomOut = useCallback(() => {
    const currentViewport = viewportRef.current;
    const size = canvasSizeRef.current;

    const newScale = Math.max(MIN_ZOOM, currentViewport.scale - ZOOM_STEP);
    const centerX = size.width / 2;
    const centerY = size.height / 2;

    const worldCenterX = (centerX - rulerOffset - currentViewport.x) / currentViewport.scale;
    const worldCenterY = (centerY - rulerOffset - currentViewport.y) / currentViewport.scale;

    const newX = centerX - rulerOffset - worldCenterX * newScale;
    const newY = centerY - rulerOffset - worldCenterY * newScale;

    onViewportChange?.({ x: newX, y: newY, scale: newScale });
  }, [onViewportChange, rulerOffset]);

  const resetView = useCallback(() => {
    onViewportChange?.({ x: 50, y: 50, scale: 1 });
  }, [onViewportChange]);

  const setZoom = useCallback(
    (scale: number) => {
      const currentViewport = viewportRef.current;
      const size = canvasSizeRef.current;

      const clampedScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, scale));
      const centerX = size.width / 2;
      const centerY = size.height / 2;

      const worldCenterX = (centerX - rulerOffset - currentViewport.x) / currentViewport.scale;
      const worldCenterY = (centerY - rulerOffset - currentViewport.y) / currentViewport.scale;

      const newX = centerX - rulerOffset - worldCenterX * clampedScale;
      const newY = centerY - rulerOffset - worldCenterY * clampedScale;

      onViewportChange?.({ x: newX, y: newY, scale: clampedScale });
    },
    [onViewportChange, rulerOffset]
  );

  const panTo = useCallback(
    (x: number, y: number) => {
      const currentViewport = viewportRef.current;
      onViewportChange?.({ ...currentViewport, x, y });
    },
    [onViewportChange]
  );

  const centerOn = useCallback(
    (worldX: number, worldY: number) => {
      const currentViewport = viewportRef.current;
      const size = canvasSizeRef.current;

      const centerX = size.width / 2;
      const centerY = size.height / 2;

      const newX = centerX - rulerOffset - worldX * currentViewport.scale;
      const newY = centerY - rulerOffset - worldY * currentViewport.scale;

      onViewportChange?.({ ...currentViewport, x: newX, y: newY });
    },
    [onViewportChange, rulerOffset]
  );

  const screenToWorld = useCallback(
    (screenX: number, screenY: number): Position => {
      const currentViewport = viewportRef.current;
      return {
        x: (screenX - currentViewport.x) / currentViewport.scale,
        y: (screenY - currentViewport.y) / currentViewport.scale,
      };
    },
    []
  );

  const worldToScreen = useCallback(
    (worldX: number, worldY: number): Position => {
      const currentViewport = viewportRef.current;
      return {
        x: worldX * currentViewport.scale + currentViewport.x,
        y: worldY * currentViewport.scale + currentViewport.y,
      };
    },
    []
  );

  const fitToContent = useCallback(
    (bounds: { minX: number; minY: number; maxX: number; maxY: number }) => {
      const size = canvasSizeRef.current;
      const contentWidth = bounds.maxX - bounds.minX;
      const contentHeight = bounds.maxY - bounds.minY;
      const padding = 40;

      const availableWidth = size.width - rulerOffset - padding * 2;
      const availableHeight = size.height - rulerOffset - padding * 2;

      if (availableWidth <= 0 || availableHeight <= 0 || contentWidth <= 0 || contentHeight <= 0) {
        return;
      }

      const scaleX = availableWidth / contentWidth;
      const scaleY = availableHeight / contentHeight;
      const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.min(scaleX, scaleY)));

      const contentCenterX = (bounds.minX + bounds.maxX) / 2;
      const contentCenterY = (bounds.minY + bounds.maxY) / 2;

      const viewAreaCenterX = rulerOffset + (size.width - rulerOffset) / 2;
      const viewAreaCenterY = rulerOffset + (size.height - rulerOffset) / 2;

      const newX = viewAreaCenterX - rulerOffset - contentCenterX * newScale;
      const newY = viewAreaCenterY - rulerOffset - contentCenterY * newScale;

      onViewportChange?.({ x: newX, y: newY, scale: newScale });
    },
    [onViewportChange, rulerOffset]
  );

  return {
    canvasRef,
    containerRef,
    canvasSize,
    zoomIn,
    zoomOut,
    resetView,
    setZoom,
    panTo,
    centerOn,
    screenToWorld,
    worldToScreen,
    fitToContent,
  };
}

export function drawGrid(
  ctx: CanvasRenderingContext2D,
  viewport: ViewportState,
  canvasWidth: number,
  canvasHeight: number,
  gridSize: number = DEFAULT_GRID_SIZE,
  isDarkMode: boolean = false,
  rulerOffset: number = 0,
  warehouseWidth?: number,
  warehouseHeight?: number
) {
  const dpr = window.devicePixelRatio || 1;
  const scaledGridSize = gridSize * viewport.scale;

  if (scaledGridSize < 5) return;

  ctx.save();
  ctx.scale(dpr, dpr);

  if (warehouseWidth !== undefined && warehouseHeight !== undefined) {
    const clipX = viewport.x + rulerOffset;
    const clipY = viewport.y + rulerOffset;
    const clipWidth = warehouseWidth * viewport.scale;
    const clipHeight = warehouseHeight * viewport.scale;

    ctx.beginPath();
    ctx.rect(Math.max(rulerOffset, clipX), Math.max(rulerOffset, clipY), clipWidth, clipHeight);
    ctx.clip();
  }

  const minorColor = isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)';
  const majorColor = isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)';

  const offsetX = ((viewport.x + rulerOffset) % scaledGridSize);
  const offsetY = ((viewport.y + rulerOffset) % scaledGridSize);

  ctx.strokeStyle = minorColor;
  ctx.lineWidth = 1;

  for (let x = offsetX + rulerOffset; x < canvasWidth; x += scaledGridSize) {
    ctx.beginPath();
    ctx.moveTo(x, rulerOffset);
    ctx.lineTo(x, canvasHeight);
    ctx.stroke();
  }

  for (let y = offsetY + rulerOffset; y < canvasHeight; y += scaledGridSize) {
    ctx.beginPath();
    ctx.moveTo(rulerOffset, y);
    ctx.lineTo(canvasWidth, y);
    ctx.stroke();
  }

  const majorGridSize = scaledGridSize * 10;
  const majorOffsetX = ((viewport.x + rulerOffset) % majorGridSize);
  const majorOffsetY = ((viewport.y + rulerOffset) % majorGridSize);

  ctx.strokeStyle = majorColor;
  ctx.lineWidth = 1;

  for (let x = majorOffsetX + rulerOffset; x < canvasWidth; x += majorGridSize) {
    ctx.beginPath();
    ctx.moveTo(x, rulerOffset);
    ctx.lineTo(x, canvasHeight);
    ctx.stroke();
  }

  for (let y = majorOffsetY + rulerOffset; y < canvasHeight; y += majorGridSize) {
    ctx.beginPath();
    ctx.moveTo(rulerOffset, y);
    ctx.lineTo(canvasWidth, y);
    ctx.stroke();
  }

  ctx.restore();
}

export function drawWarehouseOutline(
  ctx: CanvasRenderingContext2D,
  viewport: ViewportState,
  width: number,
  height: number,
  isDarkMode: boolean = false,
  rulerOffset: number = 0,
  borderColor: string = '#2563eb'
) {
  const dpr = window.devicePixelRatio || 1;

  ctx.save();
  ctx.scale(dpr, dpr);

  const screenX = viewport.x + rulerOffset;
  const screenY = viewport.y + rulerOffset;
  const screenWidth = width * viewport.scale;
  const screenHeight = height * viewport.scale;

  ctx.fillStyle = isDarkMode ? '#1e293b' : '#ffffff';
  ctx.fillRect(screenX, screenY, screenWidth, screenHeight);

  ctx.shadowColor = isDarkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.15)';
  ctx.shadowBlur = 20;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 4;
  ctx.fillStyle = isDarkMode ? '#1e293b' : '#ffffff';
  ctx.fillRect(screenX, screenY, screenWidth, screenHeight);
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 3;
  ctx.strokeRect(screenX, screenY, screenWidth, screenHeight);

  const cornerSize = Math.min(16, Math.min(screenWidth, screenHeight) * 0.1);
  const cornerThickness = 4;
  ctx.fillStyle = borderColor;

  ctx.fillRect(screenX - 1, screenY - 1, cornerSize, cornerThickness);
  ctx.fillRect(screenX - 1, screenY - 1, cornerThickness, cornerSize);

  ctx.fillRect(screenX + screenWidth - cornerSize + 1, screenY - 1, cornerSize, cornerThickness);
  ctx.fillRect(screenX + screenWidth - cornerThickness + 1, screenY - 1, cornerThickness, cornerSize);

  ctx.fillRect(screenX - 1, screenY + screenHeight - cornerThickness + 1, cornerSize, cornerThickness);
  ctx.fillRect(screenX - 1, screenY + screenHeight - cornerSize + 1, cornerThickness, cornerSize);

  ctx.fillRect(screenX + screenWidth - cornerSize + 1, screenY + screenHeight - cornerThickness + 1, cornerSize, cornerThickness);
  ctx.fillRect(screenX + screenWidth - cornerThickness + 1, screenY + screenHeight - cornerSize + 1, cornerThickness, cornerSize);

  ctx.restore();
}

export function clearCanvas(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  isDarkMode: boolean = false
) {
  const dpr = window.devicePixelRatio || 1;
  ctx.save();
  ctx.scale(dpr, dpr);

  ctx.fillStyle = isDarkMode ? '#0f172a' : '#e2e8f0';
  ctx.fillRect(0, 0, width, height);

  const patternSize = 20;
  ctx.strokeStyle = isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.04)';
  ctx.lineWidth = 1;

  for (let x = 0; x < width; x += patternSize) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }

  for (let y = 0; y < height; y += patternSize) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }

  ctx.restore();
}
